import { cn } from "../shared/ui/index.ts";
import * as React from "react";
import { appFocusPatientOnDashboard } from "../core/app-bridge.ts";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import type { Patient } from "../core/services/patients.service.ts";
import {
  patientsService,
  stubPatient,
} from "../core/services/patients.service.ts";
import { I } from "../shared/icons/icons.tsx";
import { miniIconBtn, mobileListCard, pg } from "../shared/page-layout.ts";
import { OptionStepper } from "../shared/selection-nav.component.tsx";

const statusBadgeClass = {
  done: "border border-gray-200 bg-gray-50 text-gray-400",
  current: "border border-sf-primary-100 bg-sf-primary-50 text-sf-primary",
  upcoming: "border border-sf-border bg-white text-sf-text-secondary",
} as const;

type TodayFilter = "all" | "current" | "upcoming" | "done";
type TodayStatus = keyof typeof statusBadgeClass;

interface TodayRow {
  time: string;
  patient: string;
  age: number;
  motif: string;
  status: string;
  dur: number;
  room: string;
  color: string;
}

function patientFromTodayRow(row: TodayRow, index: number): Patient {
  return (
    patientsService.findByName(row.patient) ??
    stubPatient({
      uuid: `00000000-0000-4000-8000-${String(index + 1).padStart(12, "0")}`,
      name: row.patient,
      age: row.age,
      sex: "M",
      phone: "—",
      last: "—",
      tag: row.motif,
      color: row.color,
    })
  );
}

function openPatientOnDashboard(row: TodayRow, index: number): void {
  appFocusPatientOnDashboard(patientFromTodayRow(row, index));
}

export function PatientsTodayPage() {
  const today: TodayRow[] = [
    {
      time: "08:30",
      patient: "Marie Lefèvre",
      age: 34,
      motif: "Contrôle multiattache",
      status: "done",
      dur: 30,
      room: "Cab. 1",
      color: "#fb923c",
    },
    {
      time: "09:00",
      patient: "Antoine Roux",
      age: 67,
      motif: "Renouvellement",
      status: "done",
      dur: 15,
      room: "Cab. 1",
      color: "#60a5fa",
    },
    {
      time: "09:15",
      patient: "Sophie Bernard",
      age: 28,
      motif: "Consultation",
      status: "current",
      dur: 30,
      room: "Cab. 1",
      color: "#a78bfa",
    },
    {
      time: "10:00",
      patient: "Julien Petit",
      age: 42,
      motif: "Premier rendez-vous",
      status: "upcoming",
      dur: 45,
      room: "Cab. 2",
      color: "#34d399",
    },
    {
      time: "10:45",
      patient: "Élodie Garcia",
      age: 51,
      motif: "Bilan annuel",
      status: "upcoming",
      dur: 30,
      room: "Cab. 1",
      color: "#f472b6",
    },
    {
      time: "11:15",
      patient: "Marc Dubois",
      age: 73,
      motif: "Pose bagues",
      status: "upcoming",
      dur: 15,
      room: "Cab. 1",
      color: "#fbbf24",
    },
    {
      time: "14:00",
      patient: "Camille Henry",
      age: 29,
      motif: "Résultats analyses",
      status: "upcoming",
      dur: 30,
      room: "Cab. 1",
      color: "#22d3ee",
    },
    {
      time: "14:30",
      patient: "Paul Lemaire",
      age: 39,
      motif: "Lombalgie",
      status: "upcoming",
      dur: 30,
      room: "Cab. 2",
      color: "#fb7185",
    },
    {
      time: "15:00",
      patient: "Inès Caron",
      age: 26,
      motif: "Contraception",
      status: "upcoming",
      dur: 30,
      room: "Cab. 1",
      color: "#38bdf8",
    },
    {
      time: "15:30",
      patient: "Anne Rousseau",
      age: 58,
      motif: "Suivi diabète",
      status: "upcoming",
      dur: 30,
      room: "Cab. 1",
      color: "#c084fc",
    },
  ];
  const [filter, setFilter] = React.useState<TodayFilter>("all");
  const counts = {
    all: today.length,
    done: today.filter((t) => t.status === "done").length,
    current: today.filter((t) => t.status === "current").length,
    upcoming: today.filter((t) => t.status === "upcoming").length,
  };
  const filtered =
    filter === "all" ? today : today.filter((t) => t.status === filter);
  const stMap: Record<TodayStatus, { label: string }> = {
    done: { label: "Terminé" },
    current: { label: "En cours" },
    upcoming: { label: "À venir" },
  };

  return (
    <div className={pg.page}>
      <div className={pg.toolbar}>
        <OptionStepper
          ariaLabel="Filtrer les patients du jour"
          value={filter}
          onChange={(v) => setFilter(v as TodayFilter)}
          options={[
            { value: "all", label: "Tous", badge: counts.all },
            { value: "current", label: "En cours", badge: counts.current },
            { value: "upcoming", label: "À venir", badge: counts.upcoming },
            { value: "done", label: "Terminés", badge: counts.done },
          ]}
        />
      </div>

      <div className={pg.content}>
        <div className="overflow-hidden rounded-lg border border-sf-border bg-white">
          {filtered.map((p, i) => (
            <TodayAppointmentRow
              key={`${p.time}-${p.patient}`}
              row={p}
              index={i}
              bordered={i > 0}
              statusLabel={stMap[p.status as TodayStatus].label}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TodayAppointmentRow({
  row: p,
  index: i,
  bordered,
  statusLabel,
}: {
  row: TodayRow;
  index: number;
  bordered: boolean;
  statusLabel: string;
}) {
  const isCurrent = p.status === "current";
  const patient = patientFromTodayRow(p, i);
  const open = () => openPatientOnDashboard(p, i);
  const rowProps = {
    onClick: open,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    },
  };

  return (
    <>
      <button
        type="button"
        {...rowProps}
        className={cn(
          mobileListCard(isCurrent),
          "md:hidden",
          bordered && "border-t border-sf-border",
        )}
      >
        <div className="flex w-14 shrink-0 flex-col items-start">
          <span className="text-sm font-bold tabular-nums text-sf-text">
            {p.time}
          </span>
          <span
            className={cn(
              "mt-1 rounded px-1.5 py-0.5 text-[10px] font-semibold",
              statusBadgeClass[p.status as TodayStatus],
            )}
          >
            {statusLabel}
          </span>
        </div>
        <PatientAvatar patient={patient} size={40} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-sf-text">
            {p.patient}
            <span className="font-medium text-sf-text-muted">
              {" "}
              · {p.age} ans
            </span>
          </div>
          <div className="mt-0.5 truncate text-xs text-sf-text-muted">
            {p.motif}
          </div>
          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-sf-text-muted">
            <span className="inline-flex items-center gap-1">
              <I.Clock size={11} /> {p.dur} min
            </span>
            <span>{p.room}</span>
          </div>
        </div>
        <I.ChevronRight size={16} className="shrink-0 text-sf-text-muted" />
      </button>

      <div
        role="button"
        tabIndex={0}
        {...rowProps}
        className={cn(
          "hidden cursor-pointer md:grid md:grid-cols-[90px_44px_1fr_160px_100px_110px_auto] md:items-center md:gap-3.5 md:px-4 md:py-3 md:text-[13px] md:transition-colors md:hover:bg-gray-50",
          isCurrent
            ? "md:bg-sf-primary-50 md:hover:bg-sf-primary-50"
            : "md:bg-white",
          bordered && "md:border-t md:border-sf-border",
        )}
      >
        <div className="text-sm font-bold tabular-nums tracking-tight">
          {p.time}
        </div>
        <PatientAvatar patient={patient} size={36} />
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold">
            {p.patient}{" "}
            <span className="font-medium text-sf-text-muted">
              · {p.age} ans
            </span>
          </div>
          <div className="text-xs text-sf-text-muted">{p.motif}</div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-sf-text-muted">
          <I.Clock size={12} /> {p.dur} min
        </div>
        <div className="text-xs text-sf-text-muted">{p.room}</div>
        <div>
          <span
            className={cn(
              "rounded px-2 py-0.5 text-[11px] font-semibold",
              statusBadgeClass[p.status as TodayStatus],
            )}
          >
            {statusLabel}
          </span>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            className={miniIconBtn()}
            title="Dossier"
          >
            <I.ClipboardList size={13} />
          </button>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={miniIconBtn()}
            title="Plus"
          >
            <I.Dots size={13} />
          </button>
        </div>
      </div>
    </>
  );
}
